import { ArrowRight, BookOpen, Check, Clock3, Flame, Play, Sparkles, Target, Trophy } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { useAuth } from '../app/AuthContext';
import { useProgress } from '../app/ProgressContext';
import { curriculum, phases } from '../content/curriculum';
import { ProgressRing } from '../components/ProgressRing';
import { Heatmap } from '../components/Heatmap';

const quotes = [
  'Small progress is still progress.', 'The pattern becomes obvious after you’ve earned it.',
  'Consistency turns confusion into instinct.', 'One focused hour can change your interview story.',
];

export function DashboardPage() {
  const { user } = useAuth();
  const { progress, loading } = useProgress();
  const current = curriculum.find((item) => progress.days[item.day]?.status !== 'completed') ?? curriculum[59];
  const completed = progress.stats.completedDays;
  const totalPercent = completed / 60 * 100;
  const phaseDays = curriculum.filter((item) => item.phaseNumber === current.phaseNumber);
  const phaseDone = phaseDays.filter((item) => progress.days[item.day]?.status === 'completed').length;
  const taskDone = current.tasks.filter((task) => progress.tasks[`${current.day}:${task.id}`]).length;
  const greeting = new Date().getHours() < 12 ? 'Good morning' : new Date().getHours() < 18 ? 'Good afternoon' : 'Good evening';

  if (loading) return <DashboardSkeleton />;

  return (
    <div className="page dashboard-page">
      <header className="page-heading dashboard-heading">
        <div><span className="eyebrow">{new Intl.DateTimeFormat('en', { weekday: 'long', month: 'long', day: 'numeric' }).format(new Date())}</span><h1>{greeting}, {user?.name.split(' ')[0]}.</h1><p>You’re one focused session away from a better answer.</p></div>
        <div className="header-streak"><span><Flame fill="currentColor" /></span><div><strong>{progress.stats.currentStreak}</strong><small>day streak</small></div></div>
      </header>

      <section className="dashboard-grid">
        <motion.article className="today-card" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}>
          <div className="today-top"><span className={`track-pill phase-${current.phaseNumber}`}>{current.track}</span><span><Clock3 size={15} /> {current.minutes} min</span></div>
          <div className="today-content">
            <div><span className="day-kicker">DAY {String(current.day).padStart(2, '0')} <i /> {current.phase.toUpperCase()}</span><h2>{current.title}</h2><p>{current.summary}</p></div>
            <div className="today-ring"><ProgressRing value={taskDone / current.tasks.length * 100} label="today" /></div>
          </div>
          <div className="today-tasks">{current.tasks.map((task) => <div key={task.id} className={progress.tasks[`${current.day}:${task.id}`] ? 'done' : ''}><span>{progress.tasks[`${current.day}:${task.id}`] ? <Check /> : null}</span>{task.label}</div>)}</div>
          <Link className="button primary" to={`/day/${current.day}`}><Play size={17} fill="currentColor" /> {taskDone ? 'Continue today’s lesson' : 'Start today’s lesson'} <ArrowRight size={18} /></Link>
          <div className="card-grid-pattern" />
        </motion.article>

        <motion.article className="journey-card" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: .08 }}>
          <div className="card-heading"><div><span className="eyebrow">THE JOURNEY</span><h3>Overall progress</h3></div><Link to="/roadmap">View roadmap <ArrowRight /></Link></div>
          <div className="journey-number"><strong>{completed}</strong><span>/ 60 days</span><small>{Math.round(totalPercent)}% complete</small></div>
          <div className="long-progress"><i style={{ width: `${totalPercent}%` }} /></div>
          <div className="phase-mini-list">{phases.map((phase) => {
            const days = curriculum.filter((item) => item.phaseNumber === phase.number);
            const done = days.filter((item) => progress.days[item.day]?.status === 'completed').length;
            return <div key={phase.number}><span style={{ background: phase.color }}>{phase.number}</span><div><b>{phase.name}</b><small>{done} / {days.length} days</small></div><em>{Math.round(done / days.length * 100)}%</em></div>;
          })}</div>
        </motion.article>
      </section>

      <section className="quick-stats">
        <article><span className="stat-icon green"><Target /></span><div><strong>{completed}</strong><small>Days complete</small></div><em>of 60</em></article>
        <article><span className="stat-icon orange"><Flame /></span><div><strong>{progress.stats.longestStreak}</strong><small>Best streak</small></div><em>days</em></article>
        <article><span className="stat-icon purple"><Sparkles /></span><div><strong>{progress.stats.xp.toLocaleString()}</strong><small>Experience</small></div><em>XP</em></article>
        <article><span className="stat-icon blue"><Trophy /></span><div><strong>{progress.badges.length}</strong><small>Badges earned</small></div><em>of 6</em></article>
      </section>

      <section className="dashboard-bottom">
        <article className="activity-card">
          <div className="card-heading"><div><span className="eyebrow">SHOWING UP</span><h3>Your consistency</h3></div><span className="active-legend"><i /> Active day</span></div>
          <Heatmap activity={progress.activity} weeks={14} />
          <div className="activity-note"><Flame size={18} /><span><b>{progress.stats.currentStreak ? `You’re on a ${progress.stats.currentStreak}-day streak.` : 'Your next streak starts today.'}</b> {progress.stats.currentStreak ? 'Complete one task today to keep the chain alive.' : 'One checked task is enough to begin.'}</span></div>
        </article>
        <article className="motivation-card"><div><Sparkles /></div><span className="eyebrow">TODAY’S REMINDER</span><blockquote>“{quotes[new Date().getDate() % quotes.length]}”</blockquote><p>You’re {phaseDone} days into {current.phase}. Keep the promise you made to yourself.</p></article>
      </section>
    </div>
  );
}

function DashboardSkeleton() {
  return <div className="page"><div className="skeleton title" /><div className="dashboard-grid"><div className="skeleton block" /><div className="skeleton block" /></div><div className="quick-stats"><div className="skeleton small" /><div className="skeleton small" /><div className="skeleton small" /><div className="skeleton small" /></div></div>;
}

